import React, { useEffect, useRef, useState } from "react";
import { FirebaseError } from "firebase/app";
import {
  auth,
  firebaseLogout,
  syncPushTokenIfPossible,
  readOsNotificationPermission,
  NOTIFICATION_PERMISSION_CHANGE_EVENT,
} from "../../../services/firebase";
import type { NotificationPermissionStatus } from "../../../services/notificationPermissionStatus";
import { apiClient } from "../../../services/api";
import { adoptCacheOwner } from "../../../services/queryPersistence";
import { reportedPushPermissionStore } from "../../notification/stores/reportedPushPermissionStore";
import { pushPermissionAnswer } from "../../notification/utils/pushPermissionAnswer";
import { ensureCurrentUser } from "./useCurrentUser";
import { User } from "firebase/auth";

// Firebase auth-observer error codes that mean the stored credential is
// genuinely and permanently invalid — the ONLY case that warrants a forced
// sign-out here. Everything else (network failures, rate limits, and any
// unknown or non-Firebase error) is treated as transient: we mirror Firebase's
// own local source of truth (`auth.currentUser`) rather than assuming
// logged-out, so a user restored from persistence stays authenticated through
// the blip. The old "sign out on ANY observer error" logged users out on a
// mere network hiccup.
// NOTE: `auth/requires-recent-login` is deliberately absent — it only means a
// sensitive operation needs step-up reauth; the session stays valid for normal
// use, so it must NOT force a sign-out.
const CREDENTIAL_INVALID_FIREBASE_AUTH_CODES = [
  "auth/user-token-expired",
  "auth/user-disabled",
  "auth/invalid-user-token",
  "auth/user-not-found",
] as const;

function isCredentialInvalidError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) return false;
  return CREDENTIAL_INVALID_FIREBASE_AUTH_CODES.some(
    (code) => code === error.code
  );
}

/**
 * The account write this device owes, or `null` when it has nothing to say.
 * `pushPermissionAnswer` holds the rule and the reasons; this only fetches what
 * the rule needs.
 *
 * `readOsNotificationPermission`, never `getNotificationPermissionState`: the
 * latter coerces a failed native bridge call to `"denied"`, and this value is
 * stored on the account.
 */
function rememberPermission(
  userId: string,
  status: NotificationPermissionStatus
): void {
  reportedPushPermissionStore.set({
    ...reportedPushPermissionStore.get(),
    [userId]: status,
  });
}

/**
 * Read the permission, and do whatever `pushPermissionAnswer` says about it.
 * That function holds the rule and the reasons; this only carries it out.
 *
 * `readOsNotificationPermission`, never `getNotificationPermissionState`: the
 * latter coerces a failed native bridge call to `"denied"`, and what is decided
 * here is stored on the account.
 */
async function syncPushOptOutOnce(): Promise<void> {
  // Read the permission before the account, so a signed-out visitor and a
  // device with nothing to report both cost no request.
  const observed = await readOsNotificationPermission();
  if (observed === null) return;

  const user = await ensureCurrentUser();
  if (user === null) return;

  // `Record` lookups give `undefined` for an absent key; the rule takes `null`
  // for "this device has never reported", so it is narrowed here, not
  // propagated.
  const reported = reportedPushPermissionStore.get()[user.id];
  const action = pushPermissionAnswer(
    observed,
    reported === undefined ? null : reported
  );

  if (action.kind === "say-nothing") return;
  if (action.kind === "remember") {
    rememberPermission(user.id, action.status);
    return;
  }

  try {
    await apiClient.patch("/webapp/private/users/me", {
      json: { pushOptOut: action.pushOptOut },
    });
  } catch {
    // Handled by NOT recording: the account keeps its value and the next
    // foreground, or the next permission change, retries the write. Nothing is
    // shown on purpose — the reader never asked for this, it reconciles a
    // permission they changed in the OS, and a toast about a background write
    // would be noise. The retry is the remedy. `onRequestError` already reports
    // the failure, so there is nothing to log here either.
    return;
  }

  // Re-read the identity before filing the report. `/users/me` is resolved from
  // whatever credential is current, so on a shared browser a sign-out and
  // sign-in while this was in flight would otherwise file this device's report
  // under the account that has just left — telling the new account nothing and
  // stopping the old one from ever retrying. Cached, so it costs no request.
  const stillSignedIn = await ensureCurrentUser();
  if (stillSignedIn === null || stillSignedIn.id !== user.id) return;

  // Filed only once the write has landed. A failed PATCH stays unrecorded on
  // purpose, so the next foreground retries it — which is the error handling
  // this background sync needs, and why the rejection below is logged rather
  // than surfaced.
  rememberPermission(user.id, action.status);
}

// Coalesce concurrent callers (several useFirebaseUser mounts, plus the auth,
// visibility and permission-change events, fire in parallel) into one in-flight
// PATCH; they used to race into write conflicts on the user row.
let inFlight: Promise<void> | null = null;
let lookAgain = false;

function syncPushOptOut() {
  if (inFlight !== null) {
    // Coalesced, not discarded. The run already in flight may have read the
    // permission BEFORE the reader answered the prompt, and this call is the
    // notice that they answered — dropping it left the answer unrecorded until
    // some later, unrelated foreground.
    lookAgain = true;
    return;
  }
  inFlight = syncPushOptOutOnce()
    .catch(console.error)
    .finally(() => {
      inFlight = null;
      if (!lookAgain) return;
      // One more pass, and only one: it re-reads the permission, and a pass
      // with nothing new to say stops there.
      lookAgain = false;
      syncPushOptOut();
    });
}

export function useFirebaseUser() {
  const [isLoadingFirebaseUser, setLoading] = useState(true);
  const [isAuthenticatedFirebase, setAuthenticated] = useState(
    Boolean(auth.currentUser)
  );
  // Track whether auth state actually changed (false → true = login).
  // On initial mount when already authenticated, we use a throttled sync
  // instead of force to avoid hammering the endpoint on every page.
  const prevAuthRef = useRef(isAuthenticatedFirebase);

  useEffect(() => {
    setLoading(true);
    return auth.onAuthStateChanged(
      (user: User | null) => {
        // The persisted cache is handed to this user BEFORE the session is
        // published: publishing it is what lets private pages mount, and they
        // render whatever the cache holds. Explicit sign-in, a session restored
        // at boot (one the native shell injected included), a session lost:
        // all of them pass here. See `createOwnedPersistence`.
        void (async () => {
          await adoptCacheOwner(user === null ? null : user.uid);
          setAuthenticated(user !== null);
          setLoading(false);
        })();
      },
      (error: unknown) => {
        console.error(error);
        // Only a genuinely invalid credential warrants a forced sign-out. The
        // full logout, so the cache goes with the session, and it goes before
        // "signed out" is published: desktop renders a private page under its
        // auth gate, on whatever the cache still holds.
        if (isCredentialInvalidError(error)) {
          void (async () => {
            await firebaseLogout().catch(console.error);
            setAuthenticated(false);
            setLoading(false);
          })();
          return;
        }
        // Transient / unknown error (network, rate limit): NOT an auth failure.
        // Reflect Firebase's local source of truth instead of assuming
        // logged-out — a session restored from persistence keeps
        // `auth.currentUser`, so the user stays authenticated and the route
        // guard (which keys off `firebaseUser`) does not bounce them to /login.
        setAuthenticated(auth.currentUser !== null);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (!isAuthenticatedFirebase) {
      prevAuthRef.current = false;
      return;
    }
    // Force sync only on real login transition (false → true),
    // otherwise respect the session-level throttle.
    const justLoggedIn = !prevAuthRef.current;
    prevAuthRef.current = true;
    syncPushTokenIfPossible(
      justLoggedIn ? { force: true, reason: "auth" } : { reason: "foreground" }
    ).catch(console.error);
    syncPushOptOut();
  }, [isAuthenticatedFirebase]);

  // Re-sync push token when app resumes from background (works on web + native WebViews).
  // Combined with session throttle in firebase.ts, fires at most once per 5 minutes.
  useEffect(() => {
    if (!isAuthenticatedFirebase) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncPushTokenIfPossible({ reason: "foreground" }).catch(console.error);
        syncPushOptOut();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticatedFirebase]);

  // Answering the OS prompt is the one moment the reader actually decides, and
  // it reaches neither of the effects above: the in-app request resolves
  // without an auth change, and the browser's own dialog never hides the
  // document, so no `visibilitychange` fires. Without this the reader who just
  // granted push stayed opted out until they backgrounded the app or reloaded
  // — and for the accounts whose refusal this release keeps, that is precisely
  // the moment they are trying to undo it. `requestNotificationPermission*`
  // dispatches this event on every branch.
  useEffect(() => {
    if (!isAuthenticatedFirebase) return;
    const onPermissionChange = () => {
      syncPushOptOut();
    };
    window.addEventListener(
      NOTIFICATION_PERMISSION_CHANGE_EVENT,
      onPermissionChange
    );
    return () => {
      window.removeEventListener(
        NOTIFICATION_PERMISSION_CHANGE_EVENT,
        onPermissionChange
      );
    };
  }, [isAuthenticatedFirebase]);

  return React.useMemo(
    () => ({
      firebaseUser: auth.currentUser,
      isLoadingFirebaseUser,
      isAuthenticatedFirebase,
    }),
    [isLoadingFirebaseUser, isAuthenticatedFirebase]
  );
}
